import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { I18nService } from 'core-app/core/i18n/i18n.service';
import { PathHelperService } from 'core-app/core/path-helper/path-helper.service';
import { ToastService } from 'core-app/shared/components/toaster/toast.service';
import { WorkPackageResource } from 'core-app/features/hal/resources/work-package-resource';
import idFromLink from 'core-app/features/hal/helpers/id-from-link';

interface IPainelPrincipal {
  principalId:number;
  name:string;
  email?:string|null;
  sub?:string|null;
  group:boolean;
}

interface IPainelMember extends IPainelPrincipal {
  id:number;
  role:'manager'|'member';
  pending:boolean;
  you:boolean;
}

interface IPainelState {
  canManage:boolean;
  currentUserId:number;
  members:IPainelMember[];
  directory:IPainelPrincipal[];
  error?:string|null;
}

interface IPainelSection {
  title:string;
  items:IPainelMember[];
  empty:string;
}

export const painelMembersTabSelector = 'op-painel-members-tab';

@Component({
  selector: painelMembersTabSelector,
  templateUrl: './painel-members-tab.component.html',
  styleUrls: ['./painel-members-tab.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PainelMembersTabComponent implements OnInit {
  @Input() public workPackage:WorkPackageResource;

  public state:IPainelState|null = null;
  public query = '';
  public results:{ list:IPainelPrincipal[]; invite:string|null }|null = null;
  public openMenu:number|null = null;
  public openRole:number|null = null;
  public menuStyle:Record<string, string> = {};

  private readonly emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  public text = {
    title: this.I18n.t('js.work_packages.tabs.painel', { defaultValue: 'Membros do Painel' }),
    searchPlaceholder: this.I18n.t('js.gpe.painel.search', { defaultValue: 'Nome, grupo ou email…' }),
    hint: this.I18n.t('js.gpe.painel.hint', { defaultValue: 'Quem não tem conta recebe um convite por email e entra como membro.' }),
    noResults: this.I18n.t('js.gpe.painel.no_results', { defaultValue: 'Sem resultados. Escreva um email completo para convidar.' }),
    gestores: this.I18n.t('js.gpe.painel.gestores', { defaultValue: 'Gestores' }),
    membros: this.I18n.t('js.gpe.painel.membros', { defaultValue: 'Membros' }),
    pending: this.I18n.t('js.gpe.painel.pending', { defaultValue: 'Convites pendentes' }),
    emptyGestores: this.I18n.t('js.gpe.painel.empty_gestores', { defaultValue: 'Nenhum gestor.' }),
    emptyMembros: this.I18n.t('js.gpe.painel.empty_membros', { defaultValue: 'Ainda sem membros. Adicione alguém acima.' }),
    you: this.I18n.t('js.gpe.painel.you', { defaultValue: '(você)' }),
    gestor: this.I18n.t('js.gpe.painel.gestor', { defaultValue: 'Gestor' }),
    membro: this.I18n.t('js.gpe.painel.membro', { defaultValue: 'Membro' }),
    gestorDesc: this.I18n.t('js.gpe.painel.gestor_desc', { defaultValue: 'Pode editar o painel e gerir membros.' }),
    membroDesc: this.I18n.t('js.gpe.painel.membro_desc', { defaultValue: 'Pode ver e comentar o painel.' }),
    invitePrefix: this.I18n.t('js.gpe.painel.invite_prefix', { defaultValue: 'Convidar' }),
    inviteHint: this.I18n.t('js.gpe.painel.invite_hint', { defaultValue: 'Não está no projeto — recebe um convite por email e entra como membro.' }),
    inviteBadge: this.I18n.t('js.gpe.painel.invite_badge', { defaultValue: 'Convite' }),
    resend: this.I18n.t('js.gpe.painel.resend', { defaultValue: 'Reenviar convite' }),
    makeManager: this.I18n.t('js.gpe.painel.make_manager', { defaultValue: 'Tornar gestor' }),
    makeMember: this.I18n.t('js.gpe.painel.make_member', { defaultValue: 'Tornar membro' }),
    cancelInvite: this.I18n.t('js.gpe.painel.cancel_invite', { defaultValue: 'Cancelar convite' }),
    leave: this.I18n.t('js.gpe.painel.leave', { defaultValue: 'Sair do painel' }),
    remove: this.I18n.t('js.gpe.painel.remove', { defaultValue: 'Remover do painel' }),
  };

  constructor(
    readonly http:HttpClient,
    readonly pathHelper:PathHelperService,
    readonly toastService:ToastService,
    readonly I18n:I18nService,
    readonly cdRef:ChangeDetectorRef,
  ) {}

  ngOnInit():void {
    this.load();
  }

  public get canManage():boolean {
    return !!this.state?.canManage;
  }

  public get sections():IPainelSection[] {
    const members = this.state?.members || [];
    const active = members.filter((m) => !m.pending);
    const pending = members.filter((m) => m.pending);
    const sections:IPainelSection[] = [
      { title: this.text.gestores, items: active.filter((m) => m.role === 'manager'), empty: this.text.emptyGestores },
      { title: this.text.membros, items: active.filter((m) => m.role === 'member'), empty: this.text.emptyMembros },
    ];
    if (pending.length) {
      sections.push({ title: this.text.pending, items: pending, empty: '' });
    }
    return sections;
  }

  public principalHref(p:IPainelPrincipal):string {
    return `/api/v3/${p.group ? 'groups' : 'users'}/${p.principalId}`;
  }

  public onQuery():void {
    const q = this.query.trim().toLowerCase();
    if (!q || !this.state) {
      this.results = null;
      return;
    }
    const memberEmails = new Set(this.state.members.map((m) => (m.email || '').toLowerCase()));
    const list = this.state.directory
      .filter((d) => `${d.name} ${d.email || ''}`.toLowerCase().includes(q))
      .slice(0, 6);
    const isEmail = this.emailRe.test(this.query.trim());
    const known = this.state.directory.some((d) => (d.email || '').toLowerCase() === q) || memberEmails.has(q);
    this.results = { list, invite: isEmail && !known ? this.query.trim() : null };
  }

  public clearSearch():void {
    this.query = '';
    this.results = null;
  }

  public pick(p:IPainelPrincipal):void {
    this.clearSearch();
    this.post(this.base, { painel_member: { principal_id: p.principalId, papel: 'membro' } },
      this.I18n.t('js.gpe.painel.added', { name: p.name, defaultValue: `${p.name} adicionado como membro.` }));
  }

  public pickInvite(email:string):void {
    this.clearSearch();
    this.post(`${this.base}/invite`, { email },
      this.I18n.t('js.gpe.painel.invited', { email, defaultValue: `Convite enviado para ${email}.` }));
  }

  public setRole(m:IPainelMember, papel:string):void {
    this.openRole = null;
    this.openMenu = null;
    this.patch(`${this.base}/${m.id}`, { papel });
  }

  public remove(m:IPainelMember):void {
    this.openMenu = null;
    this.del(`${this.base}/${m.id}`, m.pending
      ? this.I18n.t('js.gpe.painel.invite_cancelled', { name: m.name, defaultValue: `Convite para ${m.name} cancelado.` })
      : this.I18n.t('js.gpe.painel.removed', { name: m.name, defaultValue: `${m.name} removido do painel.` }));
  }

  public resend(m:IPainelMember):void {
    this.openMenu = null;
    this.post(`${this.base}/invite`, { email: m.email },
      this.I18n.t('js.gpe.painel.resent', { defaultValue: 'Convite reenviado.' }));
  }

  public toggleMenu(m:IPainelMember, event:MouseEvent):void {
    if (this.openMenu === m.id) {
      this.openMenu = null;
      return;
    }
    this.openRole = null;
    this.openMenu = m.id;
    this.positionMenu(event, 230);
  }

  public toggleRole(m:IPainelMember, event:MouseEvent):void {
    if (this.openRole === m.id) {
      this.openRole = null;
      return;
    }
    this.openMenu = null;
    this.openRole = m.id;
    this.positionMenu(event, 270);
  }

  // Posiciona o dropdown em position:fixed a partir do botão, virando para
  // cima quando há pouco espaço por baixo. Evita o recorte do overflow da tab.
  private positionMenu(event:MouseEvent, width:number):void {
    const btn = (event.currentTarget || event.target) as HTMLElement;
    const r = btn.getBoundingClientRect();
    const right = `${Math.max(8, window.innerWidth - r.right)}px`;
    const flipUp = (window.innerHeight - r.bottom) < 220;
    this.menuStyle = flipUp
      ? { position: 'fixed', right, bottom: `${window.innerHeight - r.top + 4}px`, width: `${width}px` }
      : { position: 'fixed', right, top: `${r.bottom + 4}px`, width: `${width}px` };
  }

  public closeMenus():void {
    this.openMenu = null;
    this.openRole = null;
  }

  private get base():string {
    const projectId = idFromLink(this.workPackage.project.href);
    return `${this.pathHelper.projectPath(projectId)}/work_packages/${this.workPackage.id}/painel_members`;
  }

  private load():void {
    this.http.get<IPainelState>(`${this.base}.json`).subscribe(
      (s) => this.apply(s),
      () => this.apply(null),
    );
  }

  private post(url:string, body:unknown, success?:string):void {
    this.http.post<IPainelState>(url, body).subscribe(
      (s) => this.applyWithSuccess(s, success),
      () => this.apply(null),
    );
  }

  private patch(url:string, body:unknown):void {
    this.http.patch<IPainelState>(url, body).subscribe(
      (s) => this.apply(s),
      () => this.apply(null),
    );
  }

  private del(url:string, success?:string):void {
    this.http.delete<IPainelState>(url).subscribe(
      (s) => this.applyWithSuccess(s, success),
      () => this.apply(null),
    );
  }

  private applyWithSuccess(state:IPainelState|null, success?:string):void {
    this.apply(state);
    if (success && state && !state.error) {
      this.toastService.addSuccess(success);
    }
  }

  private apply(state:IPainelState|null):void {
    if (!state) {
      this.toastService.addError(this.I18n.t('js.gpe.painel.error', { defaultValue: 'Ocorreu um erro.' }));
      return;
    }
    if (state.error) {
      this.toastService.addError(state.error);
    }
    this.state = state;
    this.openMenu = null;
    this.openRole = null;
    this.cdRef.detectChanges();
  }
}
